import React, { useEffect, useState, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSlideNavigation } from '../hooks/useSlideNavigation';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
// Import Slides
import { TitleSlide } from './slides/TitleSlide';
import { OverviewSlide } from './slides/OverviewSlide';
import { ContactSlide } from './slides/ContactSlide';
import { StepLayout } from './ui/StepLayout';
import { AnalysisIllustration } from './illustrations/AnalysisIllustration';
import { PrototypeIllustration } from './illustrations/PrototypeIllustration';
import { BacklogIllustration } from './illustrations/BacklogIllustration';
import { SprintIllustration } from './illustrations/SprintIllustration';
import { InvoiceIllustration } from './illustrations/InvoiceIllustration';
import { GoLiveIllustration } from './illustrations/GoLiveIllustration';
import { FoundationIllustration } from './illustrations/FoundationIllustration';
import { SupportIllustration } from './illustrations/SupportIllustration';
const STEP_ILLUSTRATIONS = [AnalysisIllustration, PrototypeIllustration, BacklogIllustration, SprintIllustration, InvoiceIllustration, GoLiveIllustration, FoundationIllustration, SupportIllustration];
const STEP_SLIDES = STEP_ILLUSTRATIONS.map((Illustration, index) => {
  function StepSlide() {
    return <StepLayout stepIndex={index} illustration={<Illustration data-id="element-164" data-mp="3dr96sfbawgie" />} data-id="element-163" data-mp="2q4148559blk2" />;
  }
  return StepSlide;
});
const SLIDES = [TitleSlide, OverviewSlide, ...STEP_SLIDES, ContactSlide];
export function Presentation() {
  const {
    currentSlide,
    direction,
    nextSlide,
    prevSlide,
    goToSlide,
    isFirst,
    isLast
  } = useSlideNavigation(SLIDES.length);
  const CurrentSlideComponent = SLIDES[currentSlide];
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const [showHint, setShowHint] = useState(true);
  // Auto-dismiss hint after 6 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(timer);
  }, []);
  // Dismiss hint on first navigation
  useEffect(() => {
    if (currentSlide > 0) {
      setShowHint(false);
    }
  }, [currentSlide]);
  return <div className="w-screen h-screen bg-bg overflow-hidden relative" data-id="element-165" data-mp="694asttz23kd">
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gray-200 z-50" data-id="element-166" data-mp="1a1474ycfpaa">
        <motion.div className="h-full bg-accent" initial={{
        width: 0
      }} animate={{
        width: `${(currentSlide + 1) / SLIDES.length * 100}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-167" data-mp="3sezpxa0mwoi4" />
      </div>

      {/* Main Slide Area */}
      <AnimatePresence initial={false} custom={direction} data-id="element-168" data-mp="3el0348mvbxao">
        <motion.div key={currentSlide} custom={direction} variants={{
        enter: (direction: number) => ({
          x: direction > 0 ? '100%' : '-100%',
          opacity: 0,
          zIndex: 0
        }),
        center: {
          x: 0,
          opacity: 1,
          zIndex: 1
        },
        exit: (direction: number) => ({
          x: direction > 0 ? '-100%' : '100%',
          opacity: 0,
          zIndex: 0
        })
      }} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-169" data-mp="39mf4gdn9lloc">
          <CurrentSlideComponent data-id="element-170" data-mp="1f92w5p7hutk2" />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Controls */}
      <div className="absolute bottom-8 left-0 w-full flex flex-col items-center gap-4 z-50 pointer-events-none" data-id="element-171" data-mp="ipdfq41pbmma">
        {/* Dots */}
        <div className="flex gap-3 pointer-events-auto" data-id="element-172" data-mp="3mc8m0qy6w66">
          {SLIDES.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-2 rounded-full transition-all duration-300 ${currentSlide === index ? 'w-8 bg-accent' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-173" data-mp="3ut58n1a08p94" data-mpi={index} />)}
        </div>
      </div>

      {/* Side Arrows */}
      {!isFirst && <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-text-secondary hover:text-primary backdrop-blur-sm transition-all z-50" data-id="element-174" data-mp="he8wfsll2jwr">
          <ChevronLeft className="w-8 h-8" data-id="element-175" data-mp="rcpsh3zdpopo" />
        </button>}

      {!isLast && <div className="absolute right-4 top-1/2 -translate-y-1/2 z-50 flex items-center" data-id="element-176" data-mp="2y3fgq3t0eljk">
          {/* Navigation Hint Tooltip */}
          <AnimatePresence data-id="element-177" data-mp="2t5mflp6kchq5">
            {showHint && isFirst && <motion.div initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 10
        }} transition={{
          delay: 1.5,
          duration: 0.4
        }} className="mr-3 flex items-center" data-id="element-178" data-mp="18mto41l4sn06">
                <div className="bg-white/95 backdrop-blur-sm text-primary text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg border border-gray-100 whitespace-nowrap" data-id="element-179" data-mp="ty5y0au215hm">
                  Hier klicken zum Weiterblättern →
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Arrow Button with pulse ring on first slide */}
          <button onClick={() => {
        setShowHint(false);
        nextSlide();
      }} className="relative p-3 rounded-full bg-white/10 hover:bg-white/20 text-text-secondary hover:text-primary backdrop-blur-sm transition-all" data-id="element-180" data-mp="2fa7e9yyxygcn">
            {/* Pulsing ring — stays visible on first slide even after tooltip fades */}
            {isFirst && <motion.div className="absolute inset-0 rounded-full border-2 border-accent" animate={{
          opacity: [0, 0.8, 0],
          scale: [1, 1.5, 1.8]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeOut'
        }} data-id="element-181" data-mp="1ywkp1w2qhb90" />}
            <ChevronRight className="w-8 h-8 relative z-10" data-id="element-182" data-mp="2htlwrono9pgn" />
          </button>
        </div>}

      {/* Legal Links — bottom right on every slide */}
      <div className="absolute bottom-3 right-4 z-50 flex items-center gap-3" data-id="element-183" data-mp="3ajawzzsk5lh7">
        <button onClick={() => setShowDatenschutz(true)} className="text-[11px] text-text-muted/60 hover:text-text-secondary transition-colors" data-id="element-184" data-mp="3ah7wglts49y8">
          Datenschutz
        </button>
        <span className="text-[11px] text-text-muted/30" data-id="element-185" data-mp="3rsdak8yvmx0h">|</span>
        <button onClick={() => setShowImpressum(true)} className="text-[11px] text-text-muted/60 hover:text-text-secondary transition-colors" data-id="element-186" data-mp="3rr24u3lvff6q">
          Impressum
        </button>
      </div>

      {/* Impressum Modal */}
      <AnimatePresence data-id="element-187" data-mp="9m8jhoqcwe3k">
        {showImpressum && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-188" data-mp="9knro6vff99e">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowImpressum(false)} data-id="element-189" data-mp="o9tnrlyywjd2" />
            <motion.div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8" initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} data-id="element-190" data-mp="1ekmv5f1ulnao">
              <button onClick={() => setShowImpressum(false)} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-text-muted hover:text-primary transition-colors" data-id="element-191" data-mp="uvmaw45x21pn">
                <X className="w-5 h-5" data-id="element-192" data-mp="zx1urswcy26g" />
              </button>
              <h2 className="text-2xl font-bold text-primary mb-6" data-id="element-193" data-mp="zvgy9xqhg95y">
                Impressum
              </h2>
              <div className="space-y-5 text-sm text-text-secondary leading-relaxed" data-id="element-194" data-mp="c4usye7v52zn">
                <div data-id="element-195" data-mp="761r5j1xv0fb">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-196" data-mp="2ivhql4of9zil">
                    Angaben gemäß § 5 TMG
                  </h3>
                  <p data-id="element-197" data-mp="2nmga0d4pjdq2">
                    INTIGOO! GmbH
                    <br data-id="element-198" data-mp="2no1461n40une" />
                    Werner-von-Siemens-Straße 33
                    <br data-id="element-199" data-mp="29wmj7p9n6jdp" />
                    49124 Georgsmarienhütte
                  </p>
                </div>
                <div data-id="element-200" data-mp="2ers3kjnebpzo">
                  <p data-id="element-201" data-mp="32jgxu5js2oj9">
                    Handelsregister: HRB 217224
                    <br data-id="element-202" data-mp="32hcmx3ngop9h" />
                    Registergericht: Amtsgericht Osnabrück
                  </p>
                </div>
                <div data-id="element-203" data-mp="2sl9j7tcyoikf">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-204" data-mp="2sm2kiet5ifv6">
                    Vertreten durch
                  </h3>
                  <p data-id="element-205" data-mp="2xisz0xpb53eh">
                    Fabian Kleine Huxel
                    <br data-id="element-206" data-mp="1h5kx147j7nko" />
                    Felix Höfelmeyer
                  </p>
                </div>
                <div data-id="element-207" data-mp="1m5h3da3pbr3e">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-208" data-mp="1m608fq896l00">Kontakt</h3>
                  <p data-id="element-209" data-mp="1xpthr73kyeow">
                    Telefon: +49 5401 1689580
                    <br data-id="element-210" data-mp="22p6fwnzwj86s" />
                    E‑Mail: info@intigoo.de
                  </p>
                </div>
                <div data-id="element-211" data-mp="1o0johnx02pz7">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-212" data-mp="1nz8nfvuxvwbs">
                    Umsatzsteuer-ID
                  </h3>
                  <p data-id="element-213" data-mp="1sxsbc1t3fi6s">
                    Umsatzsteuer-Identifikationsnummer gemäß § 27 a
                    Umsatzsteuergesetz:
                    <br data-id="element-214" data-mp="1f1y8o0iwxp24" />
                    DE 349819653
                  </p>
                </div>
                <div data-id="element-215" data-mp="151e68ujnqeib">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-216" data-mp="151xnsa5eb0vt">
                    EU-Streitschlichtung
                  </h3>
                  <p data-id="element-217" data-mp="1a1absy806dzz">
                    Die Europäische Kommission stellt eine Plattform zur
                    Online-Streitbeilegung (OS) bereit:{' '}
                    <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline" data-id="element-218" data-mp="34e9vb2fi7scj">
                      https://ec.europa.eu/consumers/odr/
                    </a>
                    <br data-id="element-219" data-mp="12hrl05sipggu" />
                    Unsere E‑Mail-Adresse finden Sie oben im Impressum.
                  </p>
                </div>
                <div data-id="element-220" data-mp="xoewrwrr3bom">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-221" data-mp="1bh4wt5446h4q">
                    Verbraucherstreitbeilegung / Universalschlichtungsstelle
                  </h3>
                  <p data-id="element-222" data-mp="16ko0voqoayh3">
                    Wir sind nicht bereit oder verpflichtet, an
                    Streitbeilegungsverfahren vor einer
                    Verbraucherschlichtungsstelle teilzunehmen.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Datenschutz Modal */}
      <AnimatePresence data-id="element-223" data-mp="3i9ozr1ifa9u9">
        {showDatenschutz && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-224" data-mp="brdj2cq2oktv">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDatenschutz(false)} data-id="element-225" data-mp="pqwrl6mspy6p" />
            <motion.div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8" initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} data-id="element-226" data-mp="unwx9gfqgyxa">
              <button onClick={() => setShowDatenschutz(false)} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-text-muted hover:text-primary transition-colors" data-id="element-227" data-mp="1iesni94jjm2f">
                <X className="w-5 h-5" data-id="element-228" data-mp="2xwowad0ypq2x" />
              </button>
              <h2 className="text-2xl font-bold text-primary mb-6" data-id="element-229" data-mp="2synd7s3jdqrg">
                Datenschutzerklärung
              </h2>
              <div className="space-y-5 text-sm text-text-secondary leading-relaxed" data-id="element-230" data-mp="2o1dqik7j7t7b">
                <div data-id="element-231" data-mp="3lmsycfyi5hrt">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-232" data-mp="3lnb546isjedz">
                    1. Verantwortlicher
                  </h3>
                  <p data-id="element-233" data-mp="3gr3wg5z0fosc">
                    Die verantwortliche Stelle für die Datenverarbeitung auf
                    dieser Website ist:
                  </p>
                  <p className="mt-2" data-id="element-234" data-mp="36y6kyjsfr038">
                    INTIGOO! GmbH
                    <br data-id="element-235" data-mp="897px73j0k6k" />
                    Werner-von-Siemens-Straße 33
                    <br data-id="element-236" data-mp="3i7rfxerdu1y" />
                    49124 Georgsmarienhütte
                  </p>
                  <p className="mt-2" data-id="element-237" data-mp="3k5lxb7v4vsw6">
                    Telefon: +49 5401 4977 0<br data-id="element-238" data-mp="3k414bmogwi0f" />
                    E‑Mail: info@intigoo.de
                  </p>
                  <p className="mt-2" data-id="element-239" data-mp="3p5gntaazyggp">
                    Verantwortliche Stelle ist die natürliche oder juristische
                    Person, die allein oder gemeinsam mit anderen über die
                    Zwecke und Mittel der Verarbeitung von personenbezogenen
                    Daten (z. B. Namen, E‑Mail-Adressen o. Ä.) entscheidet.
                  </p>
                </div>
                <div data-id="element-240" data-mp="bor4ymbypqwc">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-241" data-mp="bp0wiecno645">
                    2. Hosting
                  </h3>
                  <p data-id="element-242" data-mp="3t4g0xi6vtjj8">
                    Diese Website wird bei Amazon Web Services (AWS) gehostet.
                  </p>
                  <p className="mt-2" data-id="element-243" data-mp="ppts72ep178u">
                    Anbieter in Europa ist:
                    <br data-id="element-244" data-mp="uga5mmiiksv7" />
                    Amazon Web Services EMEA SARL
                    <br data-id="element-245" data-mp="uhldaxf2ykrl" />
                    38 Avenue John F. Kennedy
                    <br data-id="element-246" data-mp="2f5e8yxxqef9g" />
                    L-1855 Luxemburg
                  </p>
                  <p className="mt-2" data-id="element-247" data-mp="2a8e24kt258ha">
                    Beim Aufruf dieser Website werden durch den Hosting-Anbieter
                    automatisch folgende Daten verarbeitet:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1" data-id="element-248" data-mp="2jygtkgkj6edi">
                    <li data-id="element-249" data-mp="1w7l4hr7glx9y">IP-Adresse</li>
                    <li data-id="element-250" data-mp="1w8mjg6rlj3wy">Datum und Uhrzeit des Zugriffs</li>
                    <li data-id="element-251" data-mp="1r9067xbrpno5">Browsertyp und Browserversion</li>
                    <li data-id="element-252" data-mp="1rakzzkq9d2ky">verwendetes Betriebssystem</li>
                    <li data-id="element-253" data-mp="25917vezuwz4o">Referrer-URL</li>
                    <li data-id="element-254" data-mp="259ug6kegrwox">Hostname des zugreifenden Rechners</li>
                  </ul>
                  <p className="mt-2" data-id="element-255" data-mp="2164vi60wtz96">
                    Diese Daten werden in sogenannten Server-Logfiles
                    gespeichert.
                  </p>
                  <p className="mt-2" data-id="element-256" data-mp="3cmmeb3bl43dc">
                    Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1
                    lit. f DSGVO (berechtigtes Interesse an der sicheren und
                    stabilen Bereitstellung der Website).
                  </p>
                  <p className="mt-2" data-id="element-257" data-mp="2ec3desuuhnis">
                    Mit AWS besteht ein Vertrag zur Auftragsverarbeitung gemäß
                    Art. 28 DSGVO.
                  </p>
                  <p className="mt-2" data-id="element-258" data-mp="2na5jvnxcl8dg">
                    Sofern eine Verarbeitung außerhalb der Europäischen Union
                    erfolgt, geschieht dies auf Grundlage geeigneter Garantien
                    gemäß Art. 44 ff. DSGVO (insbesondere
                    EU-Standardvertragsklauseln).
                  </p>
                </div>
                <div data-id="element-259" data-mp="24cm8idnf3dnv">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-260" data-mp="24d4c5vcesu5t">
                    3. Cookies
                  </h3>
                  <p data-id="element-261" data-mp="17tokxvju7qco">
                    Diese Website verwendet ausschließlich technisch notwendige
                    Cookies, soweit dies für den Betrieb erforderlich ist.
                  </p>
                  <p className="mt-2" data-id="element-262" data-mp="14bqwhuj9foph">
                    Es werden keine Analyse-, Tracking- oder Marketing-Cookies
                    eingesetzt.
                  </p>
                  <p className="mt-2" data-id="element-263" data-mp="vasxjqnoi7dr">
                    Eine Einwilligung über ein Cookie-Banner ist daher nicht
                    erforderlich.
                  </p>
                </div>
                <div data-id="element-264" data-mp="ldoazppx89wf">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-265" data-mp="le6g7buk9ygh">
                    4. Speicherdauer
                  </h3>
                  <p data-id="element-266" data-mp="gklh1gh8eyfp">
                    Server-Logfiles werden vom Hosting-Anbieter automatisch nach
                    einem angemessenen Zeitraum gelöscht.
                  </p>
                </div>
                <div data-id="element-267" data-mp="7k6ru615cz6x">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-268" data-mp="2mnd750jekeg">
                    5. Ihre Rechte
                  </h3>
                  <p data-id="element-269" data-mp="3tu9cklg840pi">Sie haben das Recht auf:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1" data-id="element-270" data-mp="3tvkfyk5rb6g3">
                    <li data-id="element-271" data-mp="1ll1hk867xx68">Auskunft (Art. 15 DSGVO)</li>
                    <li data-id="element-272" data-mp="1qjcw2w7gkvus">Berichtigung (Art. 16 DSGVO)</li>
                    <li data-id="element-273" data-mp="1qitjzw4hp1oo">Löschung (Art. 17 DSGVO)</li>
                    <li data-id="element-274" data-mp="1ckmvv7pceg27">Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                    <li data-id="element-275" data-mp="1hiwr1ib2hgnt">Datenübertragbarkeit (Art. 20 DSGVO)</li>
                    <li data-id="element-276" data-mp="1hh3k01wjkufa">Widerspruch (Art. 21 DSGVO)</li>
                  </ul>
                  <p className="mt-2" data-id="element-277" data-mp="12t8pcfyz4fzk">
                    Zur Ausübung Ihrer Rechte genügt eine formlose Mitteilung an
                    die oben genannten Kontaktdaten.
                  </p>
                </div>
                <div data-id="element-278" data-mp="tuwzig5xlm86">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-279" data-mp="triybb907j2b">
                    6. Beschwerderecht
                  </h3>
                  <p data-id="element-280" data-mp="jumon27dcqw6">
                    Sie haben das Recht, sich bei einer
                    Datenschutz-Aufsichtsbehörde zu beschweren.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}